import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { teacherModuleAPI } from '../../services/api';

export default function TeacherSessionApprovals() {
  const pendingQuery = useQuery(['teacherPendingApprovals'], async () => {
    const res = await teacherModuleAPI.listPendingApprovals();
    return res.data?.data || [];
  });

  const [decision, setDecision] = useState(null); // { sessionId, action }
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const close = () => {
    setDecision(null);
    setReason('');
  };

  const decide = async () => {
    if (!decision?.sessionId || !decision?.action) return;
    setSaving(true);
    try {
      await teacherModuleAPI.decideSessionApproval(decision.sessionId, {
        status: decision.action,
        reason: reason || null,
      });
      close();
      pendingQuery.refetch();
    } finally {
      setSaving(false);
    }
  };

  if (pendingQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (pendingQuery.isError) {
    return <Alert severity="error">Failed to load approval requests.</Alert>;
  }

  const rows = pendingQuery.data || [];

  return (
    <Box>
      <Typography variant="h4" fontWeight={800} gutterBottom>
        Approvals
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Approve teacher requests to fill missing attendance/homework/notes for past sessions.
      </Typography>

      {rows.length === 0 ? (
        <Alert severity="info">No pending approvals.</Alert>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Batch</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Requested by</TableCell>
                  <TableCell>Teacher reason</TableCell>
                  <TableCell>Requested at</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.session_id} hover>
                    <TableCell>{r.batch_name || 'Batch'}</TableCell>
                    <TableCell>{r.session_date}</TableCell>
                    <TableCell>
                      {String(r.start_time || '').slice(0, 5)}–{String(r.end_time || '').slice(0, 5)}
                    </TableCell>
                    <TableCell>{r.requested_by_email || r.requested_by_name || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      {r.request_reason ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {r.request_reason}
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{r.requested_at ? new Date(r.requested_at).toLocaleString() : '-'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => setDecision({ sessionId: r.session_id, action: 'rejected' })}
                        >
                          Reject
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setDecision({ sessionId: r.session_id, action: 'approved' })}
                        >
                          Approve
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={!!decision} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{decision?.action === 'approved' ? 'Approve request' : 'Reject request'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button variant="contained" onClick={decide} disabled={saving}>
            {saving ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

